import React, { useEffect } from "react";
import DamperPositionRow from "./DamperPositionRow";
import Spinner from "components/Indicators/Spinner";
import Error from "components/Indicators/Error";
import ContentFrame from "components/ContentFrame/ContentFrame";

import { useDispatch, useSelector } from "react-redux";
import { DamperData, Tag, StoreState } from "models";
import { fetchDampersData, UpdateDamperValue, ToggleOverrideAll } from "redux/Dampers/Dampers.actions";
import { Typography, Checkbox, FormControlLabel, Tooltip } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/styles";
import { ValmetGrey4 } from "../../theme";
import { timeToUTCTimeStamp } from "utils/time.utils";

const localStyles = makeStyles(() =>
  createStyles({
    damperTitle: {
      marginBottom: "20px",
      marginTop: "20px",
      textAlign: "left",
      color: ValmetGrey4,
    },
  }),
);

const DamperPositions = () => {
  const dispatch = useDispatch();
  const localClasses = localStyles();

  const selectedMill = useSelector((state: StoreState) => state.millSelector.selectedMill);
  const lineId = useSelector((state: StoreState) => state.millSelector.selectedLineId);

  const startTime = useSelector((state: StoreState) => state.timeSelector.startTime);
  const endTime = useSelector((state: StoreState) => state.timeSelector.endTime);

  const DampersData: DamperData[] = useSelector((state: StoreState) => state.dampers.data);
  const isFetching: boolean = useSelector((state: StoreState) => state.dampers.fetching);
  const didInvalidate: boolean = useSelector((state: StoreState) => state.dampers.didInvalidate);

  const fetchTime: string = useSelector((state: StoreState) => state.dampers.fetchTime);
  const fetchLine: string = useSelector((state: StoreState) => state.dampers.fetchLine);

  const overrideAll: boolean = useSelector((state: StoreState) => state.dampers.overrideAll);

  const gmtOffset: number = useSelector((state: StoreState) =>
    state.millSelector.selectedMill ? parseInt(state.millSelector.selectedMill.offset) : 0,
  );

  useEffect(() => {
    if (!selectedMill || !lineId || !startTime) return;

    const { startTimeStamp, endTimeStamp } = timeToUTCTimeStamp(new Date(startTime), gmtOffset);

    if (startTimeStamp !== fetchTime || lineId !== fetchLine) {
      const reloadDampers = lineId !== fetchLine;
      dispatch(fetchDampersData(selectedMill.millId, lineId, startTimeStamp, endTimeStamp, reloadDampers));
    }
  }, [selectedMill, lineId, startTime, endTime, fetchTime, fetchLine, gmtOffset, dispatch]);

  const handleChangeValue = (type: string, tag_name: string, update: { newPosition?: number; override?: boolean }): void => {
    dispatch(UpdateDamperValue({ type, tag_name, update }));
  };

  const chooseValue = (tag: Tag) => {
    if (tag.override && tag.newPosition !== undefined && tag.newPosition !== null) {
      return tag.newPosition;
    }

    return tag.position;
  };

  const handleOverrideAll = () => {
    dispatch(ToggleOverrideAll(!overrideAll));
  };

  const renderTags = (damper: DamperData, tag: Tag) => {
    return (
      <div key={tag.tag_name}>
        <DamperPositionRow
          key={tag.tag_name}
          tagName={tag.tag_name}
          description={tag.description}
          value={chooseValue(tag)}
          type={damper.type}
          override={tag.override ?? false}
          changeValue={handleChangeValue}
        />
        {tag.tags ? (
          <>
            {tag.tags!.map((localTag) => (
              <DamperPositionRow
                key={localTag.tag_name}
                tagName={localTag.tag_name}
                description={localTag.description}
                value={chooseValue(localTag)}
                type={damper.type}
                override={localTag.override ?? false}
                changeValue={handleChangeValue}
              />
            ))}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <ContentFrame title="Damper Positions">
      {isFetching && !didInvalidate ? (
        <Spinner message="Loading damper positions"></Spinner>
      ) : didInvalidate ? (
        <Error message="Loading damper positions has failed"></Error>
      ) : (
        <section>
          <Tooltip title={overrideAll ? "Use default values" : "Override all damper positions"} placement="top">
            <FormControlLabel
              control={<Checkbox checked={overrideAll} onChange={handleOverrideAll} name="overrideAll" color="primary" />}
              label={"Override all"}
            />
          </Tooltip>
          {DampersData
            ? DampersData.map((damper: DamperData) => (
              <div key={damper.type}>
                <Typography variant="h3" className={localClasses.damperTitle}>
                  {damper.type}
                </Typography>

                {damper.tags.map((tag: Tag, index: number) => renderTags(damper, tag))}
              </div>
            ))
            : null}
        </section>
      )}
    </ContentFrame>
  );
};

export default React.memo(DamperPositions);
